import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarTick, MessageRemove, MessageTick, Send2 } from 'iconsax-react';
import { StatCard } from '@src/features/dashboard/components/stats-card';
import reminderApi from '@src/features/reminders/services/reminder-api';
import { DefaultDateVlue } from '@src/features/dashboard/constants';
import { getDateRangeObject } from '@src/features/dashboard/helper/date-helper';
import { formatPercentDisplay } from '../../../utils/format-percent';

export function ReminderOverviewStats() {
  const [params] = useSearchParams();
  const selectedChannels = useMemo(
    () => params.getAll('channel'),
    [params.toString()]
  );
  const selectedPrograms = useMemo(
    () => params.getAll('programs[]'),
    [params.toString()]
  );
  const dateFilter = params.get('date') || DefaultDateVlue;

  const queryParams = useMemo(() => {
    const range = getDateRangeObject(dateFilter);
    const out: Record<string, string | string[]> = {};
    if (range) {
      out.start_date = range.start_date;
      out.end_date = range.end_date;
    }
    if (selectedChannels.length > 0) out.channel = selectedChannels;
    if (selectedPrograms.length > 0) out.programs = selectedPrograms;
    return out;
  }, [dateFilter, selectedChannels, selectedPrograms]);

  const { data: statsData, isLoading } =
    reminderApi.useGetReminderStats(queryParams);

  const totalSent = statsData?.total_sent ?? 0;
  const scheduled = statsData?.scheduled ?? 0;
  const deliveryRate = formatPercentDisplay(statsData?.delivery_rate);
  const failed = statsData?.failed ?? 0;

  return (
    <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
      <StatCard
        title='Total Sent'
        value={totalSent}
        className='h-[105px]'
        cardContentClassName='gap-0'
        valueClassName='text-primary-800'
        icon={
          <Send2
            className='text-primary-1000'
            size={24}
          />
        }
        isLoading={isLoading}
      />
      <StatCard
        title='Scheduled'
        value={scheduled}
        className='h-[105px]'
        cardContentClassName='gap-0'
        valueClassName='text-secondary-1000'
        icon={
          <CalendarTick
            className='text-primary-1000'
            size={24}
          />
        }
        isLoading={isLoading}
      />
      <StatCard
        title='Delivery Rate'
        value={deliveryRate}
        className='h-[105px]'
        cardContentClassName='gap-0'
        valueClassName='text-green-500'
        icon={
          <MessageTick
            className='text-primary-1000'
            size={24}
          />
        }
        isLoading={isLoading}
      />
      <StatCard
        title='Failed'
        value={failed}
        className='h-[105px]'
        cardContentClassName='gap-0'
        valueClassName='text-red-400'
        icon={
          <MessageRemove
            className='text-primary-1000'
            size={24}
          />
        }
        isLoading={isLoading}
      />
    </div>
  );
}
